import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  categoryLabel,
  effectivePriceCents,
  formatBytes,
  ENTITLEMENT_SOURCE_LABELS,
  PLATFORM_LABELS,
  productMedia,
  type CheckoutResponse,
  type DownloadTicket,
  type ProductDetail,
} from '@va/shared';
import { api, errorMessage } from '../api/client';
import { useProduct } from '../api/queries';
import { IconArrowLeft, IconCart, IconCheck, IconDownload, IconFolder, IconMonitor, IconPlay, IconTrash } from '../components/icons';
import { ProductCard } from '../components/ProductCard';
import { daysUntil, ErrorBox, formatDate, Loading, Markdown, PlatformIcons, Price } from '../components/ui';
import { YouTube } from '../components/YouTube';
import { desktop, isDesktop, mediaUrl, needsUpdate } from '../platform';
import { useAuth } from '../state/auth';
import { useCart } from '../state/cart';
import { installedMessage, pickBuild, useInstalls } from '../state/installs';
import { useToast } from '../state/toast';

function MediaViewer({ product }: { product: ProductDetail }) {
  // In the order chosen in the admin (the first one opens first). The short hover loop
  // (previewVideoUrl) only plays on the store's product cards, not here.
  const items = useMemo(() => productMedia(product), [product]);
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [product.id]);
  const current = items[index];

  return (
    <div>
      <div className="viewer">
        {!current && <div className="placeholder" style={{ display: 'grid', placeItems: 'center', height: '100%', fontFamily: 'var(--va-font-display)', fontSize: 40, color: 'var(--va-indigo-light)' }}>{product.name}</div>}
        {current?.kind === 'video' && <video key={current.src} src={mediaUrl(current.src)} poster={mediaUrl(product.thumbnailUrl)} autoPlay muted loop playsInline controls={false} />}
        {current?.kind === 'image' && <img key={current.src} src={mediaUrl(current.src)} alt={product.name} />}
        {current?.kind === 'youtube' && <YouTube key={current.id} id={current.id} title={product.name} />}
      </div>
      {items.length > 1 && (
        <div className="thumbs" role="tablist" aria-label="Media">
          {items.map((it, i) => (
            <button key={it.key + i} className={`thumb${i === index ? ' on' : ''}`} onClick={() => setIndex(i)} role="tab" aria-selected={i === index} aria-label={`Show ${it.kind} ${i + 1}`}>
              {it.kind === 'image' && <img src={mediaUrl(it.src)} alt="" loading="lazy" />}
              {it.kind === 'video' && <video src={mediaUrl(it.src)} muted preload="metadata" />}
              {it.kind === 'youtube' && <img src={`https://i.ytimg.com/vi/${it.id}/mqdefault.jpg`} alt="" loading="lazy" />}
              {it.kind !== 'image' && (
                <span className="play">
                  <IconPlay size={18} />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InstallControls({ product, owned }: { product: ProductDetail; owned: boolean }) {
  const installs = useInstalls();
  const toast = useToast();
  const build = pickBuild(product.builds);
  const installed = installs.get(product.id);
  const progress = installs.progress[product.id];

  if (!installs.available) return null;
  if (!build) {
    return <div className="notice small">No download for your computer yet{product.platforms.length ? ` (available for ${product.platforms.map((p) => PLATFORM_LABELS[p]).join(' and ')})` : ''}.</div>;
  }
  if (!owned && product.demoMode === 'none') return null;

  const run = async (fn: () => Promise<void>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (progress) {
    const pct = progress.total ? Math.round((progress.received / progress.total) * 100) : 0;
    return (
      <div className="stack" style={{ gap: 8 }}>
        <div className="row between small">
          <span className="mono">{progress.phase === 'download' ? `Downloading… ${pct}%` : progress.phase === 'verify' ? 'Verifying…' : 'Installing…'}</span>
          <span className="dim">{formatBytes(progress.received)} / {formatBytes(progress.total)}</span>
        </div>
        <div className="progress"><div style={{ width: `${progress.phase === 'download' ? pct : 100}%` }} /></div>
      </div>
    );
  }

  if (installed) {
    const update = needsUpdate(installed, build);
    return (
      <div className="stack" style={{ gap: 10 }}>
        <div className="row between small">
          <span className="pill">Installed · v{installed.version}</span>
          <span className="row" style={{ gap: 4 }}>
            <button className="btn btn-sm btn-ghost" onClick={() => installs.reveal(product.id)} title={installed.appDir ? 'Show the plugin folder' : 'Show in folder'}><IconFolder size={15} /></button>
            {installed.appDir && (
              <button className="btn btn-sm btn-ghost" onClick={() => installs.reveal(product.id, 'app')} title="Opens the app's folder in Downloads">
                <IconMonitor size={15} /> Show app
              </button>
            )}
            <button className="btn btn-sm btn-ghost" onClick={() => run(() => installs.uninstall(product.id), `${product.name} removed.`)} title="Uninstall"><IconTrash size={15} /></button>
          </span>
        </div>
        {update && (
          <button className="btn btn-indigo btn-block" onClick={() => run(() => installs.install(product, build), installedMessage(product.name, build.installType, { demo: !owned, updatedTo: build.version }))}>
            <IconDownload size={16} /> Update to v{build.version}
          </button>
        )}
        {!owned && <p className="small muted" style={{ margin: 0 }}>Running in demo mode with the VA watermark.</p>}
      </div>
    );
  }

  // Demos are installed from the Library, after "Try".
  if (!owned) return null;
  return (
    <button className="btn btn-primary btn-lg btn-block" onClick={() => run(() => installs.install(product, build), installedMessage(product.name, build.installType))}>
      <IconDownload size={16} /> Install <span className="dim">· {formatBytes(build.sizeBytes)}</span>
    </button>
  );
}

function WebDownloads({ product }: { product: ProductDetail }) {
  const toast = useToast();
  if (product.builds.length === 0) return <p className="small muted">Downloads will appear here when a build is published.</p>;
  const download = async (id: string) => {
    try {
      const t = await api.post<DownloadTicket>(`/downloads/${id}`);
      window.location.href = t.url;
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <div className="stack" style={{ gap: 8 }}>
      {product.builds.map((b) => (
        <button key={b.id} className="btn btn-outline btn-block" onClick={() => download(b.id)}>
          <IconDownload size={16} /> {b.platform.startsWith('mac') ? 'macOS' : b.platform.startsWith('win') ? 'Windows' : 'Download'} · v{b.version}
          <span className="dim">{formatBytes(b.sizeBytes)}</span>
        </button>
      ))}
    </div>
  );
}

function BuyBox({ product }: { product: ProductDetail }) {
  const { user } = useAuth();
  const cart = useCart();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  const own = product.ownership;
  const owned = !!own?.owned && !own.sources.every((s) => s === 'trial');
  const trialActive = !!own?.owned && own.sources.every((s) => s === 'trial');
  const price = effectivePriceCents(product);
  const inCart = cart.has(product.id);

  const requireLogin = () => {
    if (!user) {
      nav('/login', { state: { from: `/products/${product.slug}` } });
      return true;
    }
    return false;
  };

  const getFree = async () => {
    if (requireLogin()) return;
    setBusy('free');
    try {
      await api.post<CheckoutResponse>('/checkout', { productIds: [product.id], provider: 'stripe', source: isDesktop ? 'desktop' : 'web' });
      if (desktop) await desktop.license.sync();
      await qc.invalidateQueries();
      toast.success(`${product.name} is in My Library.`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  // "Try": the product goes in the Library as a demo, to install from there.
  const tryIt = async () => {
    if (requireLogin()) return;
    setBusy('try');
    try {
      await api.post('/library/demos', { productId: product.id });
      await qc.invalidateQueries({ queryKey: ['library'] });
      toast.success(
        isDesktop
          ? `${product.name} is in My Library. Press Install to try it with the VA watermark.`
          : `${product.name} is in My Library. Install it with the VA Plugin Store app to try it.`,
      );
      nav('/library');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const startTrial = async () => {
    if (requireLogin() || !desktop) return;
    setBusy('trial');
    try {
      const status = await desktop.license.status();
      await api.post('/trials', { productId: product.id, machineId: status.machineId });
      await desktop.license.sync();
      await qc.invalidateQueries();
      toast.success(`Your ${product.trialDays}-day trial of ${product.name} has started. No watermark until it ends.`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <aside className="buy-box">
      <div className="row wrap" style={{ gap: 8 }}>
        <span className="pill indigo">{categoryLabel(product.categories)}</span>
        {product.isNew && <span className="pill solid">New</span>}
        {product.latestVersion && <span className="pill muted">v{product.latestVersion}</span>}
      </div>
      <h1>{product.name}</h1>
      <p className="tagline">{product.tagline}</p>

      {owned ? (
        <div className="owned-banner">
          <IconCheck size={18} />
          <span>
            In My Library{own?.sources.length ? ` · ${own.sources.map((s) => ENTITLEMENT_SOURCE_LABELS[s]).join(', ')}` : ''}
            {own?.expiresAt ? ` · until ${formatDate(own.expiresAt)}` : ''}
          </span>
        </div>
      ) : (
        <div className="buy-price">
          <Price {...product} />
        </div>
      )}
      {trialActive && (
        <div className="notice small">
          Trial active · {Math.max(0, daysUntil(own!.expiresAt) ?? 0)} days left. Buy now to keep it without the watermark.
        </div>
      )}

      <div className="buy-actions">
        {!owned && price === 0 && (
          <button className="btn btn-primary btn-lg btn-block" onClick={getFree} disabled={busy === 'free'}>
            {busy === 'free' ? 'Adding…' : 'Get it free'}
          </button>
        )}
        {!owned && price > 0 && (
          <>
            <button
              className="btn btn-primary btn-lg btn-block"
              onClick={() => {
                cart.add(product.id);
                nav('/cart');
              }}
            >
              Buy now
            </button>
            <button className="btn btn-outline btn-block" onClick={() => (inCart ? nav('/cart') : (cart.add(product.id), toast.success(`${product.name} added to your cart.`)))}>
              <IconCart size={16} /> {inCart ? 'In cart · view cart' : 'Add to cart'}
            </button>
          </>
        )}
        {!owned && !trialActive && price > 0 && product.demoMode === 'watermark' && (
          <button className="btn btn-outline btn-block" onClick={tryIt} disabled={busy === 'try'} title="Free to use with the moving VA watermark until you buy it">
            {busy === 'try' ? 'Adding to My Library…' : 'Try it free'}
          </button>
        )}
        {!owned && !trialActive && product.trialDays > 0 && isDesktop && (
          <button className="btn btn-indigo btn-block" onClick={startTrial} disabled={busy === 'trial'}>
            Start {product.trialDays}-day full trial
          </button>
        )}

        <InstallControls product={product} owned={owned || trialActive} />

        {!isDesktop && owned && <WebDownloads product={product} />}
      </div>

      <dl className="facts card flat" style={{ margin: 0 }}>
        <dt>Platforms</dt>
        <dd className="row" style={{ gap: 8 }}>
          <PlatformIcons platforms={product.platforms} /> {product.platforms.map((p) => PLATFORM_LABELS[p]).join(', ')}
        </dd>
        {product.hosts.length > 0 && (
          <>
            <dt>Works in</dt>
            <dd>{product.hosts.join(', ')}</dd>
          </>
        )}
        <dt>License</dt>
        <dd>Your account, on up to 3 computers</dd>
        {product.demoMode === 'watermark' && (
          <>
            <dt>Demo</dt>
            <dd>Free with the moving VA watermark{product.trialDays > 0 ? `, or a ${product.trialDays}-day full trial` : ''}</dd>
          </>
        )}
      </dl>
    </aside>
  );
}

export function ProductPage() {
  const { slug } = useParams();
  const { data: product, error, isLoading, refetch } = useProduct(slug);
  // Opened from the store's page 2 (or a category or search): "Store" goes back to that list.
  const from = (useLocation().state as { from?: string } | null)?.from;
  const storeLink = from === '/' || from?.startsWith('/?') ? from : '/';

  useEffect(() => {
    if (product) document.title = `${product.name} · VA Plugin Store`;
    return () => {
      document.title = 'VA Plugin Store';
    };
  }, [product]);

  if (isLoading) return <Loading />;
  if (error || !product)
    return (
      <div className="container">
        <ErrorBox error={error ?? new Error('Product not found.')} onRetry={() => refetch()} />
      </div>
    );

  return (
    <div className="container">
      <Link to={storeLink} className="btn btn-ghost btn-sm" style={{ marginBottom: 16, paddingLeft: 8 }}>
        <IconArrowLeft size={16} /> Store
      </Link>
      <div className="product-layout">
        <div className="pl-media">
          <MediaViewer product={product} />
        </div>
        <div className="pl-buy">
          <BuyBox product={product} />
        </div>
        <div className="pl-content">
          <section className="section">
            <h2>About</h2>
            <Markdown source={product.description} />
          </section>

          {product.features.length > 0 && (
            <section className="section">
              <h2>{product.category === 'bundle' ? "What's inside" : 'Highlights'}</h2>
              <ul className="feature-list">
                {product.features.map((f) => (
                  <li key={f}>
                    <IconCheck size={16} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {product.youtubeIds.length > 0 && (
            <section className="section">
              <h2>Videos</h2>
              <div className="videos">
                {productMedia(product).flatMap((m) => (m.kind === 'youtube' ? [<YouTube key={m.id} id={m.id} title={product.name} />] : []))}
              </div>
            </section>
          )}

          {product.requirements && (
            <section className="section">
              <h2>Requirements</h2>
              <Markdown source={product.requirements} />
            </section>
          )}

          {product.bundleItems.length > 0 && (
            <section className="section">
              <h2>Included in this bundle</h2>
              <div className="grid">
                {product.bundleItems.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </section>
          )}

          {product.builds.some((b) => b.releaseNotes) && (
            <section className="section">
              <h2>What's new</h2>
              <div className="stack">
                {product.builds
                  .filter((b) => b.releaseNotes)
                  .map((b) => (
                    <div key={b.id} className="card flat">
                      <div className="row between" style={{ marginBottom: 8 }}>
                        <span className="mono" style={{ color: 'var(--va-headline)' }}>v{b.version}</span>
                        <span className="small dim">{formatDate(b.createdAt)}</span>
                      </div>
                      <Markdown source={b.releaseNotes} />
                    </div>
                  ))}
              </div>
            </section>
          )}

          {product.includedIn.length > 0 && (
            <section className="section">
              <h2>Save with a bundle</h2>
              <div className="grid">
                {product.includedIn.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
